import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { API_CONFIG } from "./apiConfig";
import { formatDate } from "./dateUtils";

export function getReportTheme() {
  const isDemo = API_CONFIG.isDemo;

  if (isDemo) {
    return {
      isDemo: true,
      brandName: "ANAXIS TECH",
      companyName: "ANAXIS TECH",
      subTitle: "Innovating Your Imagination",
      logoSrc: "/logo/demo/anaxis-logo.png",
      primaryColor: [10, 56, 102],       // #0A3866 (Deep Navy)
      secondaryColor: [0, 140, 214],    // #008CD6 (Cyan)
      headerBg: [10, 56, 102],          // Navy
      headerText: [255, 255, 255],
      footerBg: [235, 243, 250],        // Soft navy/cyan tint
      footerText: [10, 56, 102],
      borderColor: [189, 215, 238],
      altRowBg: [248, 251, 255],
      subHeaderBg: [224, 238, 249],
      excelHeaderBg: "#0A3866",
      excelFooterBg: "#EBF3FA",
      excelFooterText: "#0A3866",
    };
  }

  // Ferrovant Global (for stage, live, or local)
  return {
    isDemo: false,
    brandName: "FERROVANT GLOBAL",
    companyName: "FERROVANT GLOBAL LLP",
    subTitle: "Metal Segregation and Trading ERP",
    logoSrc: "/logo/png/logo-01.png",
    primaryColor: [197, 160, 89],       // #C5A059 (Ferrovant Signature Gold)
    secondaryColor: [173, 112, 41],     // #AD7029 (Bronze/Dark Gold)
    headerBg: [197, 160, 89],          // #C5A059 Gold
    headerText: [255, 255, 255],
    footerBg: [250, 245, 235],         // Light gold cream
    footerText: [112, 69, 31],          // Deep bronze text
    borderColor: [225, 215, 195],       // Soft gold border
    altRowBg: [253, 251, 247],
    subHeaderBg: [247, 234, 209],       // Light gold-tinted
    excelHeaderBg: "#C5A059",
    excelFooterBg: "#FAF4E6",
    excelFooterText: "#70451F",
  };
}

export function loadLogoImage(logoSrc) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = logoSrc;
  });
}

export function drawPdfHeader(doc, theme, logoImg, title) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;
  const contentWidth = pageWidth - margin * 2;

  // Top Accent Bar (Brand primary color)
  doc.setFillColor(...theme.primaryColor);
  doc.rect(margin, 8, contentWidth, 1.8, "F");

  // Logo or Fallback Text
  const curY = 12;
  const logoWidth = 34;
  const logoHeight = 12;

  if (logoImg) {
    try {
      doc.addImage(logoImg, "PNG", margin, curY, logoWidth, logoHeight);
    } catch (e) {
      doc.setFontSize(13);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...theme.primaryColor);
      doc.text(theme.brandName, margin, curY + 8);
    }
  } else {
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...theme.primaryColor);
    doc.text(theme.brandName, margin, curY + 8);
  }

  // Vertical Separator
  const sepX = margin + logoWidth + 4;
  doc.setDrawColor(...theme.borderColor);
  doc.setLineWidth(0.4);
  doc.line(sepX, curY, sepX, curY + logoHeight);

  // Company Name & Subtitle on Left
  const textX = sepX + 4;
  doc.setFontSize(8.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...(theme.isDemo ? [10, 56, 102] : [112, 69, 31]));
  doc.text(theme.companyName || theme.brandName, textX, curY + 4.5);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(100, 116, 139);
  doc.text(theme.subTitle || "Metal Segregation and Trading ERP", textX, curY + 9.5);

  // Report Title on Right
  doc.setFontSize(12.5);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...theme.primaryColor);
  doc.text(title, pageWidth - margin, curY + 5, { align: "right" });

  // Date on Right
  doc.setFontSize(7.5);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 116, 139);
  const now = new Date();
  const dateStr = "Generated: " + now.toLocaleDateString("en-GB") + " " + now.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  doc.text(dateStr, pageWidth - margin, curY + 10, { align: "right" });

  // Bottom Divider Line
  doc.setDrawColor(...theme.borderColor);
  doc.setLineWidth(0.5);
  doc.line(margin, curY + 16, pageWidth - margin, curY + 16);

  return curY + 21;
}

export function formatNumber(value) {
  const number = Number(value || 0);
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(number);
}

export function formatWeight(value) {
  return `${formatNumber(value)} kg`;
}

export function formatCurrency(value) {
  const number = Number(value || 0);
  return `₹ ${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(number)}`;
}

export function formatRate(value) {
  const number = Number(value || 0);
  return `₹ ${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 4,
    minimumFractionDigits: 2,
  }).format(number)}`;
}

export function getDetailAddition(detail) {
  return Number(
    detail?.addition ??
      detail?.addedStock ??
      detail?.additionStock ??
      detail?.additionWeight ??
      detail?.addedWeight ??
      0,
  );
}

export function getDetailDeduction(detail) {
  return Number(
    detail?.deduction ??
      detail?.deductedStock ??
      detail?.deductionStock ??
      detail?.deductionWeight ??
      detail?.deductedWeight ??
      0,
  );
}

export function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

export function getExcelNumberFormatStyle(value) {
  const num = Number(value || 0);
  return Number.isInteger(num)
    ? "mso-number-format:'\\#\\,\\#\\#0';"
    : "mso-number-format:'\\#\\,\\#\\#0\\.##';";
}

export function generateExcelHtmlFromData(rows) {
  let htmlRows = [];

  // Main Title
  htmlRows.push(`
    <tr>
      <td colspan="8" style="font-size: 14px; font-weight: bold; text-align: center; padding: 10px; background-color: #f1f5f9; border: 1px solid #cbd5e1;">
        STOCK REPORT
      </td>
    </tr>
    <tr><td colspan="8" style="height: 5px;"></td></tr>
  `);

  if (!rows || rows.length === 0) {
    htmlRows.push(`
      <tr>
        <td colspan="8" style="text-align: center; padding: 15px; color: #64748b; font-size: 12px;">No records found</td>
      </tr>
    `);
  } else {
    rows.forEach((item, index) => {
      // Category Header Table (2 rows: labels, then values)
      const addition = Math.round(Number(item.totalAddition || 0) * 100) / 100;
      const deduction = Math.round(Number(item.totalDeduction || 0) * 100) / 100;
      const processed = Math.round(Number(item.processedWeight || 0) * 100) / 100;
      const remaining = Math.round(Number(item.remainingStock || 0) * 100) / 100;

      htmlRows.push(`
        <tr style="color: #000000; font-weight: bold; text-align: center; font-size: 11px;">
          <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 5px; width: 50px;">Sr. No.</td>
          <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 5px; text-align: left;">Stock Category</td>
          <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 5px; text-align: right; width: 110px;">Addition (KG)</td>
          <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 5px; text-align: right; width: 110px;">Deduction (KG)</td>
          <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 5px; text-align: right; width: 110px;">Processed (KG)</td>
          <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 5px; text-align: right; width: 110px;">Remaining Stock (KG)</td>
          <td colspan="2" style="background-color: #f8fafc; border: none;"></td>
        </tr>
        <tr style="font-size: 11px; font-weight: bold;">
          <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 5px; text-align: center;">${index + 1}</td>
          <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 5px; text-align: left;">${escapeHtml(item.stockCategoryName)}</td>
          <td x:num="${addition}" style="${getExcelNumberFormatStyle(addition)} background-color: #ffffff; border: 1px solid #94a3b8; padding: 5px; text-align: right; color: #008000;">${addition}</td>
          <td x:num="${deduction}" style="${getExcelNumberFormatStyle(deduction)} background-color: #ffffff; border: 1px solid #94a3b8; padding: 5px; text-align: right; color: #ff0000;">${deduction}</td>
          <td x:num="${processed}" style="${getExcelNumberFormatStyle(processed)} background-color: #ffffff; border: 1px solid #94a3b8; padding: 5px; text-align: right; color: #6366f1;">${processed}</td>
          <td x:num="${remaining}" style="${getExcelNumberFormatStyle(remaining)} background-color: #ffffff; border: 1px solid #94a3b8; padding: 5px; text-align: right; color: #0000ff;">${remaining}</td>
          <td colspan="2" style="background-color: #f8fafc; border: none;"></td>
        </tr>
        <tr><td colspan="8" style="height: 3px;"></td></tr>
      `);

      // Transaction Details Sub-table
      const stockData = item.stockData || [];
      if (stockData.length > 0) {
        htmlRows.push(`
          <tr style="color: #000000; font-weight: bold; text-align: center; font-size: 10px;">
            <td style="border: none;"></td>
            <td style="background-color: #C6E0B4; border: 1px solid #94a3b8; padding: 4px; width: 85px;">Date</td>
            <td style="background-color: #C6E0B4; border: 1px solid #94a3b8; padding: 4px; width: 120px;">Lot</td>
            <td style="background-color: #C6E0B4; border: 1px solid #94a3b8; padding: 4px; text-align: right; width: 90px;">Addition (KG)</td>
            <td style="background-color: #C6E0B4; border: 1px solid #94a3b8; padding: 4px; text-align: right; width: 90px;">Deduction (KG)</td>
            <td style="background-color: #C6E0B4; border: 1px solid #94a3b8; padding: 4px; text-align: right; width: 90px;">Processed (KG)</td>
            <td style="background-color: #C6E0B4; border: 1px solid #94a3b8; padding: 4px; text-align: right; width: 100px;">Cumulative (KG)</td>
            <td style="border: none;"></td>
          </tr>
        `);

        stockData.forEach((detail) => {
          const add = Math.round(getDetailAddition(detail) * 100) / 100;
          const ded = Math.round(getDetailDeduction(detail) * 100) / 100;
          const proc = Math.round(Number(detail.processedWeight || 0) * 100) / 100;
          const cum = Math.round(Number(detail.cumulativeStock || 0) * 100) / 100;

          htmlRows.push(`
            <tr style="font-size: 10px;">
              <td style="border: none;"></td>
              <td style="background-color: #ffffff; border: 1px solid #cbd5e1; padding: 4px; text-align: center;">${escapeHtml(formatDate(detail.transactionDate))}</td>
              <td style="background-color: #ffffff; border: 1px solid #cbd5e1; padding: 4px; text-align: center; font-weight: 500;">${escapeHtml(detail.lotNo || detail.sourceLotNo || "-")}</td>
              <td x:num="${add}" style="${getExcelNumberFormatStyle(add)} background-color: #ffffff; border: 1px solid #cbd5e1; padding: 4px; text-align: right; color: #008000;">${add > 0 ? add : 0}</td>
              <td x:num="${ded}" style="${getExcelNumberFormatStyle(ded)} background-color: #ffffff; border: 1px solid #cbd5e1; padding: 4px; text-align: right; color: #ff0000;">${ded > 0 ? ded : 0}</td>
              <td x:num="${proc}" style="${getExcelNumberFormatStyle(proc)} background-color: #ffffff; border: 1px solid #cbd5e1; padding: 4px; text-align: right; color: #6366f1;">${proc > 0 ? proc : 0}</td>
              <td x:num="${cum}" style="${getExcelNumberFormatStyle(cum)} background-color: #ffffff; border: 1px solid #cbd5e1; padding: 4px; text-align: right; font-weight: bold; color: #0000ff;">${cum}</td>
              <td style="border: none;"></td>
            </tr>
          `);
        });
      }

      // Spacer between categories
      htmlRows.push(`<tr><td colspan="8" style="height: 10px; border: none;"></td></tr>`);
    });
  }

  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8" />
<style>
  table { border-collapse: collapse; font-family: Calibri, Arial, sans-serif; font-size: 11px; }
</style>
</head>
<body>
<table>
${htmlRows.join("\n")}
</table>
</body>
</html>`;
}

export function generatePDFFromData(rows, logoImg, theme = getReportTheme()) {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let finalY = drawPdfHeader(doc, theme, logoImg, "STOCK REPORT");

  if (!rows || rows.length === 0) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139);
    doc.text("No records found", pageWidth / 2, finalY + 15, { align: "center" });
    return doc;
  }

  rows.forEach((item, index) => {
    if (finalY > 235) {
      doc.addPage();
      finalY = 20;
    }

    autoTable(doc, {
      startY: finalY,
      head: [["Sr. No.", "Stock Category", "Addition (KG)", "Deduction (KG)", "Remaining Stock (KG)"]],
      body: [[
        index + 1,
        item.stockCategoryName,
        formatWeight(item.totalAddition),
        formatWeight(item.totalDeduction),
        formatWeight(item.processedWeight),
        formatWeight(item.remainingStock),
      ]],
      theme: "grid",
      headStyles: { 
        fillColor: theme.headerBg, 
        textColor: theme.headerText, 
        halign: "center",
        fontSize: 9,
        fontStyle: "bold",
        lineWidth: 0.1,
        lineColor: theme.borderColor,
      },
      bodyStyles: { 
        fontSize: 9, 
        fontStyle: "bold",
        lineWidth: 0.1
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 15 },
        1: { halign: "center" },
        2: { halign: "right", textColor: [22, 163, 74] },
        3: { halign: "right", textColor: [220, 38, 38] },
        4: { halign: "right", textColor: [99, 102, 241] },
        5: { halign: "right", textColor: [37, 99, 235] },
      },
      margin: { left: 10, right: 10 },
    });

    finalY = doc.lastAutoTable.finalY;

    const stockData = item.stockData || [];
    const detailsBody = stockData.map(detail => {
      const add = getDetailAddition(detail);
      const ded = getDetailDeduction(detail);
      return [
        formatDate(detail.transactionDate),
        detail.lotNo || detail.sourceLotNo || "-",
        add > 0 ? `+${formatWeight(add)}` : "-",
        ded > 0 ? `-${formatWeight(ded)}` : "-",
        detail.processedWeight > 0 ? formatWeight(detail.processedWeight) : "-",
        formatWeight(detail.cumulativeStock)
      ];
    });

    if (detailsBody.length > 0) {
      autoTable(doc, {
        startY: finalY,
        head: [["Date", "Lot", "Addition (KG)", "Deduction (KG)", "Processed (KG)", "Cumulative (KG)"]],
        body: detailsBody,
        theme: "grid",
        headStyles: { 
          fillColor: theme.subHeaderBg, 
          textColor: theme.isDemo ? [10, 56, 102] : [112, 69, 31], 
          halign: "center",
          fontSize: 8,
          fontStyle: "bold",
          lineWidth: 0.1
        },
        bodyStyles: { 
          fontSize: 8, 
          lineWidth: 0.1
        },
        columnStyles: {
          0: { halign: "center" },
          1: { halign: "center", fontStyle: "bold" },
          2: { halign: "center" },
          3: { halign: "center" },
          4: { halign: "right", textColor: [22, 163, 74] },
          5: { halign: "right", textColor: [220, 38, 38] },
          6: { halign: "right", textColor: [99, 102, 241] },
          7: { halign: "right", textColor: [37, 99, 235], fontStyle: "bold" },
        },
        margin: { left: 25, right: 10 },
      });
      finalY = doc.lastAutoTable.finalY + 10;
    } else {
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text("No transaction records found.", 30, finalY + 5);
      finalY += 15;
    }
  });

  return doc;
}

export function generateSummaryExcelHtmlFromData(rows) {
  let htmlRows = [];

  // Main Title
  htmlRows.push(`
    <tr>
      <td colspan="5" style="font-size: 14px; font-weight: bold; text-align: center; padding: 10px; background-color: #f1f5f9; border: 1px solid #cbd5e1;">
        STOCK SUMMARY REPORT
      </td>
    </tr>
    <tr><td colspan="5" style="height: 5px;"></td></tr>
  `);

  if (!rows || rows.length === 0) {
    htmlRows.push(`
      <tr>
        <td colspan="5" style="text-align: center; padding: 15px; color: #64748b; font-size: 12px;">No records found</td>
      </tr>
    `);
  } else {
    // Header
    htmlRows.push(`
      <tr style="color: #000000; font-weight: bold; text-align: center; font-size: 12px;">
        <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 6px; width: 60px;">Sr. No.</td>
        <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 6px; text-align: left;">Stock Category</td>
        <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 6px; text-align: right; width: 140px;">Addition (KG)</td>
        <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 6px; text-align: right; width: 140px;">Deduction (KG)</td>
        <td style="background-color: #BDD7EE; border: 1px solid #94a3b8; padding: 6px; text-align: right; width: 140px;">Remaining Stock (KG)</td>
      </tr>
    `);

    let grandAddition = 0;
    let grandDeduction = 0;
    let grandRemaining = 0;
    const startRow = 4;
    const endRow = 3 + rows.length;

    rows.forEach((item, index) => {
      const addition = Math.round(Number(item.totalAddition || 0) * 100) / 100;
      const deduction = Math.round(Number(item.totalDeduction || 0) * 100) / 100;
      const remaining = Math.round(Number(item.remainingStock || 0) * 100) / 100;

      grandAddition += addition;
      grandDeduction += deduction;
      grandRemaining += remaining;

      htmlRows.push(`
        <tr style="font-size: 12px;">
          <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: center;">${index + 1}</td>
          <td style="background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: left; font-weight: 500;">${escapeHtml(item.stockCategoryName)}</td>
          <td x:num="${addition}" style="${getExcelNumberFormatStyle(addition)} background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: right; color: #008000; font-weight: bold;">${addition}</td>
          <td x:num="${deduction}" style="${getExcelNumberFormatStyle(deduction)} background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: right; color: #ff0000; font-weight: bold;">${deduction}</td>
          <td x:num="${remaining}" style="${getExcelNumberFormatStyle(remaining)} background-color: #ffffff; border: 1px solid #94a3b8; padding: 6px; text-align: right; color: #0000ff; font-weight: bold;">${remaining}</td>
        </tr>
      `);
    });

    grandAddition = Math.round(grandAddition * 100) / 100;
    grandDeduction = Math.round(grandDeduction * 100) / 100;
    grandRemaining = Math.round(grandRemaining * 100) / 100;

    // Grand Total Row
    htmlRows.push(`
      <tr style="font-size: 12px; font-weight: bold;">
        <td style="background-color: #D9E1F2; border: 2px solid #06295B; padding: 7px; text-align: center;"></td>
        <td style="background-color: #D9E1F2; border: 2px solid #06295B; padding: 7px; text-align: left; color: #06295B;">GRAND TOTAL</td>
        <td x:num="${grandAddition}" x:fmla="=SUM(C${startRow}:C${endRow})" style="${getExcelNumberFormatStyle(grandAddition)} background-color: #D9E1F2; border: 2px solid #06295B; padding: 7px; text-align: right; color: #008000;">${grandAddition}</td>
        <td x:num="${grandDeduction}" x:fmla="=SUM(D${startRow}:D${endRow})" style="${getExcelNumberFormatStyle(grandDeduction)} background-color: #D9E1F2; border: 2px solid #06295B; padding: 7px; text-align: right; color: #ff0000;">${grandDeduction}</td>
        <td x:num="${grandRemaining}" x:fmla="=SUM(E${startRow}:E${endRow})" style="${getExcelNumberFormatStyle(grandRemaining)} background-color: #D9E1F2; border: 2px solid #06295B; padding: 7px; text-align: right; color: #0000ff;">${grandRemaining}</td>
      </tr>
    `);
  }

  return `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8" />
<style>
  table { border-collapse: collapse; font-family: Calibri, Arial, sans-serif; font-size: 11px; }
</style>
</head>
<body>
<table>
${htmlRows.join("\n")}
</table>
</body>
</html>`;
}

export function generateSummaryPDFFromData(rows, logoImg, theme = getReportTheme()) {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const startY = drawPdfHeader(doc, theme, logoImg, "STOCK SUMMARY REPORT");

  if (!rows || rows.length === 0) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139);
    doc.text("No records found", pageWidth / 2, startY + 15, { align: "center" });
    return doc;
  }

  let grandAddition = 0;
  let grandDeduction = 0;
  let grandRemaining = 0;

  const tableBody = rows.map((item, index) => {
    const addition = Number(item.totalAddition || 0);
    const deduction = Number(item.totalDeduction || 0);
    const remaining = Number(item.remainingStock || 0);

    grandAddition += addition;
    grandDeduction += deduction;
    grandRemaining += remaining;

    return [
      index + 1,
      item.stockCategoryName || "-",
      formatWeight(addition),
      formatWeight(deduction),
      formatWeight(remaining),
    ];
  });

  const tableFoot = [
    [
      { content: "", styles: { halign: "center" } },
      { content: "GRAND TOTAL", styles: { halign: "left", fontStyle: "bold", textColor: theme.footerText } },
      { content: formatWeight(grandAddition), styles: { halign: "right", fontStyle: "bold", textColor: [22, 163, 74] } },
      { content: formatWeight(grandDeduction), styles: { halign: "right", fontStyle: "bold", textColor: [220, 38, 38] } },
      { content: formatWeight(grandRemaining), styles: { halign: "right", fontStyle: "bold", textColor: [37, 99, 235] } },
    ],
  ];

  autoTable(doc, {
    startY: startY,
    head: [["Sr. No.", "Stock Category", "Addition (KG)", "Deduction (KG)", "Remaining Stock (KG)"]],
    body: tableBody,
    foot: tableFoot,
    theme: "grid",
    headStyles: {
      fillColor: theme.headerBg,
      textColor: theme.headerText,
      halign: "center",
      fontSize: 9,
      fontStyle: "bold",
      lineWidth: 0.1,
      lineColor: theme.borderColor,
    },
    bodyStyles: {
      fontSize: 9,
      lineWidth: 0.1,
      lineColor: [226, 232, 240],
    },
    alternateRowStyles: {
      fillColor: theme.altRowBg,
    },
    footStyles: {
      fillColor: theme.footerBg,
      textColor: theme.footerText,
      fontStyle: "bold",
      fontSize: 9.5,
      lineWidth: 0.3,
      lineColor: theme.primaryColor,
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 18 },
      1: { halign: "left" },
      2: { halign: "right", textColor: [22, 163, 74], fontStyle: "bold" },
      3: { halign: "right", textColor: [220, 38, 38], fontStyle: "bold" },
      4: { halign: "right", textColor: [37, 99, 235], fontStyle: "bold" },
    },
    didParseCell: (data) => {
      if (data.section === "foot") {
        if (data.column.index >= 2) {
          data.cell.styles.halign = "right";
        }
      }
    },
    didDrawPage: (data) => {
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(7.5);
      doc.setTextColor(148, 163, 184);
      doc.text(
        `${theme.brandName} • Stock Summary Report`,
        10,
        doc.internal.pageSize.getHeight() - 8
      );
      doc.text(
        `Page ${pageCount}`,
        pageWidth - 10,
        doc.internal.pageSize.getHeight() - 8,
        { align: "right" }
      );
    },
  });

  return doc;
}

